<script setup lang="ts">
/**
 * 本文中のマークにホバー/クリックしたときに、そのヒットの詳細を出すフローティングパネル。
 *
 * 【なぜ左カラムの固定ペインではなくフローティングなのか】
 * 固定ペインは位置が安定する反面、本文(右カラム)と詳細(左カラム)が離れるため、
 * 結局カラム間の目線移動とスクロールが発生してしまい、元の課題が解決しない。
 * そこで、マークの【真下に少し離して】浮かせることで、視線移動をほぼゼロにする。
 *
 * 【本文が隠れないための工夫】
 *  - マークの下端から 12px 空けて表示する(ホバーしている行自体は絶対に隠れない)
 *  - 画面下部に収まらない場合のみ、マークの上側へ回り込ませる
 *  - Teleport で body 直下に描画し、親の overflow に切られないようにする
 *
 * 【中にカーソルを入れられるようにする】
 * 参考リンクを踏めるよう、離脱は遅延させ、パネル自身へのホバーで閉じるのを取り消す。
 * クリックで固定(ピン留め)すれば、マウスを外しても内容が変わらない。
 */
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useNgMarkStore } from '@/stores/ngMark'
import { levelBucket } from '@/utils/ngHighlight'
import { openExternal } from '@/utils/links'

const ngMark = useNgMarkStore()
const hits = computed(() => ngMark.detailHits)

const PANEL_WIDTH = 500
const GAP = 12 // マークとパネルの間隔(「少し離して」の実体)
const MARGIN = 10 // 画面端からの最小余白

const panelEl = ref<HTMLElement | null>(null)
const panelHeight = ref(0)

/** 実際の高さを測ってから配置を決める(上下反転の判定に必要) */
async function measure() {
  await nextTick()
  if (panelEl.value) panelHeight.value = panelEl.value.offsetHeight
}
watch(hits, (v) => { if (v) measure() }, { immediate: true })

const style = computed(() => {
  const a = ngMark.anchor
  if (!a) return { display: 'none' }

  const vw = window.innerWidth
  const vh = window.innerHeight

  // 横: マークの左端に合わせ、画面外へはみ出す場合のみ内側へ寄せる
  let left = a.left
  if (left + PANEL_WIDTH > vw - MARGIN) left = vw - MARGIN - PANEL_WIDTH
  if (left < MARGIN) left = MARGIN

  // 縦: 原則マークの下。収まらない場合のみ上側へ回り込ませる
  const h = panelHeight.value || 190
  const below = a.bottom + GAP
  const flipUp = below + h > vh - MARGIN && a.top - GAP - h > MARGIN
  const top = flipUp ? a.top - GAP - h : below

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    width: `${PANEL_WIDTH}px`,
    maxHeight: `${Math.max(180, vh - top - MARGIN)}px`,
  }
})

// スクロールすると基準位置がずれるため、固定中でなければ閉じる
function onScroll() {
  if (!ngMark.pinned) ngMark.setHovered(null)
}
onMounted(() => window.addEventListener('scroll', onScroll, true))
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll, true))
</script>

<template>
  <Teleport to="body">
    <div
      v-if="hits && hits.length > 0"
      ref="panelEl"
      class="ng-float"
      :style="style"
      @mouseenter="ngMark.cancelClose()"
      @mouseleave="ngMark.scheduleClose()"
    >
      <div class="ng-float-head">
        <span>この箇所に {{ hits.length }} 件</span>
        <button
          v-if="ngMark.pinned"
          class="ng-float-unpin"
          title="固定を解除"
          @click="ngMark.clear()"
        >
          固定中 ✕
        </button>
      </div>

      <div v-for="(h, i) in hits" :key="`${h.word}-${i}`" class="ng-float-item">
        <div class="ng-float-item-head">
          <span class="ng-float-word">{{ h.word }}</span>
          <span class="ng-float-lv" :class="`lv${levelBucket(h.level)}`">Lv.{{ h.level }}</span>
          <a
            v-if="h.link"
            class="ng-float-link"
            title="参考リンクを開く"
            @click="openExternal(h.link!)"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            参考
          </a>
        </div>
        <p class="ng-float-reason">{{ h.reason || '(説明なし)' }}</p>
      </div>

      <div v-if="!ngMark.pinned" class="ng-float-foot">クリックで固定できます</div>
    </div>
  </Teleport>
</template>
